import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { IGridLayoutResponse, IGridState } from '../models/grid-layout.models';
import { etagFromResponse, formatEtagForQuery, parseEtag } from '../utils/etag.util';
import { ApiService } from './api.service';
import { HttpService } from './http.service';

const GRID_LAYOUT_BASE_VERSION = 0;

/**
 * Service for managing DevExtreme grid layouts with backend persistence.
 * Implements caching strategy using SessionStorage for performance.
 *
 * Caching Strategy:
 * - SessionStorage: Used for caching layouts to provide instant UI updates
 *   - Persists only for the current browser session/tab
 *   - Automatically cleared when tab/window is closed
 *   - Ensures fresh data on each new session
 *   - Reduces backend calls within the same session
 *   - Cache is invalidated when backend version changes
 */
@Injectable({
    providedIn: 'root',
})
export class GridLayoutService {
    private readonly httpService = inject(HttpService);
    private readonly apiService = inject(ApiService);

    private readonly CACHE_PREFIX = 'grid_layout_cache_';
    private readonly CACHE_VERSION_PREFIX = 'grid_layout_version_';

    /**
     * Load grid layout from backend or cache
     * @param key Grid identifier
     * @returns Observable with layout state object or null if not found
     */
    loadLayout(key: string): Observable<{ state: IGridState; version: string | null } | null> {
        // Try cache first for instant UI
        const cached = this.getCachedLayout(key);
        if (cached) {
            // Return cached version immediately
            return of(cached);
        }

        // No cache, fetch from backend
        return this.fetchLayoutFromBackend(key);
    }

    /**
     * Save grid layout to backend
     * @param key Grid identifier
     * @param state Layout state object
     * @param version Current version for optimistic concurrency (optional)
     * @returns Observable with updated version
     */
    saveLayout(key: string, state: IGridState, gridVersion?: number | null, version?: string | null): Observable<string | null> {
        // Stringify the state object - backend expects a stringified JSON object
        const stateString = JSON.stringify(state);
        const url = `${this.apiService.pathUrl.gridLayout(key)}/gridLayoutVersion/${gridVersion ?? GRID_LAYOUT_BASE_VERSION}`;
        let finalUrl = url;

        // Add version parameter if provided (quoted ETag query value)
        const versionParam = formatEtagForQuery(version);
        if (versionParam) {
            const params = this.apiService.params({ version: versionParam });
            const paramsString = params.toString();
            finalUrl = paramsString ? `${url}?${paramsString}` : url;
        }

        // Send stringified JSON string with OData Content-Type header (backend stores as string in database)
        // Backend expects: Content-Type: application/json;odata.metadata=minimal;odata.streaming=true
        // Body: plain string (not JSON object)
        return this.httpService.putWithResponse$<IGridLayoutResponse | string>(finalUrl, stateString).pipe(
            map((response: HttpResponse<IGridLayoutResponse | string>) => {
                const responseBody = response.body;
                let newVersion: string | null = null;

                // Handle different response formats
                if (typeof responseBody === 'string') {
                    // Backend returns plain string (no version in response)
                    newVersion = null;
                } else if (responseBody && typeof responseBody === 'object' && 'version' in responseBody) {
                    // Backend returns { value: string, version?: string }
                    newVersion = parseEtag(responseBody.version);
                }

                // Update cache with new version
                this.setCachedLayout(key, state, newVersion);

                return newVersion;
            }),
            catchError(error => {
                console.error(`Error saving grid layout for key "${key}":`, error);
                throw error;
            })
        );
    }

    /**
     * Fetch layout from backend
     * @param key Grid identifier
     * @returns Observable with layout state and version
     */
    private fetchLayoutFromBackend(key: string): Observable<{ state: IGridState; version: string | null } | null> {
        const url = this.apiService.pathUrl.gridLayout(key);

        return this.httpService.getWithResponse$<IGridLayoutResponse | string>(url).pipe(
            map((response: HttpResponse<IGridLayoutResponse | string>) => {
                let version: string | null = null;
                const responseBody = response.body as string;

                if (!responseBody) {
                    return { state: null, version };
                }

                // ETag header must be exposed via Access-Control-Expose-Headers: ETag
                version = etagFromResponse(response);

                // Parse the stringified state back to object
                let state: IGridState;
                try {
                    state = typeof responseBody === 'string' ? (JSON.parse(responseBody) as IGridState) : responseBody;
                } catch (parseError) {
                    console.error(`Failed to parse grid layout state for key "${key}":`, parseError);
                    return null;
                }

                // Cache the layout
                this.setCachedLayout(key, state, version);

                return { state, version };
            }),
            catchError(error => {
                // 404 means no layout exists yet, which is fine
                if (error.status === 404) {
                    return of(null);
                }
                console.error(`Error loading grid layout for key "${key}":`, error);
                return of(null);
            })
        );
    }

    /**
     * Get cached layout from SessionStorage
     * @param key Grid identifier
     * @returns Cached layout with version or null
     */
    private getCachedLayout(key: string): { state: IGridState; version: string | null } | null {
        try {
            const cacheKey = `${this.CACHE_PREFIX}${key}`;
            const versionKey = `${this.CACHE_VERSION_PREFIX}${key}`;

            const cachedState = sessionStorage.getItem(cacheKey);
            const cachedVersion = sessionStorage.getItem(versionKey);

            if (!cachedState) {
                return null;
            }

            return {
                state: JSON.parse(cachedState) as IGridState,
                version: cachedVersion,
            };
        } catch (error) {
            console.error(`Error reading cached layout for key "${key}":`, error);
            return null;
        }
    }

    /**
     * Set cached layout in SessionStorage
     * @param key Grid identifier
     * @param state Layout state object
     * @param version Version string
     */
    private setCachedLayout(key: string, state: IGridState, version: string | null): void {
        try {
            const cacheKey = `${this.CACHE_PREFIX}${key}`;
            const versionKey = `${this.CACHE_VERSION_PREFIX}${key}`;

            sessionStorage.setItem(cacheKey, JSON.stringify(state));
            const cleanVersion = parseEtag(version);
            if (cleanVersion) {
                sessionStorage.setItem(versionKey, cleanVersion);
            }
        } catch (error) {
            console.error(`Error caching layout for key "${key}":`, error);
        }
    }

    /**
     * Delete all grid layouts for the given identifier from backend and clear cache
     * @param key Grid identifier
     * @returns Observable that completes when deletion is done
     */
    deleteLayout(key: string): Observable<void> {
        const url = this.apiService.pathUrl.gridLayout(key);

        return this.httpService.delete$<void>(url).pipe(
            tap(() => this.clearCache(key)),
            catchError(error => {
                // 404 means layout was already removed
                if (error.status === 404) {
                    this.clearCache(key);
                    return of(undefined);
                }
                console.error(`Error deleting grid layout for key "${key}":`, error);
                throw error;
            })
        );
    }

    /**
     * Clear cached layout from SessionStorage
     * @param key Grid identifier
     */
    clearCache(key: string): void {
        try {
            const cacheKey = `${this.CACHE_PREFIX}${key}`;
            const versionKey = `${this.CACHE_VERSION_PREFIX}${key}`;

            sessionStorage.removeItem(cacheKey);
            sessionStorage.removeItem(versionKey);
        } catch (error) {
            console.error(`Error clearing cache for key "${key}":`, error);
        }
    }

    /**
     * Clear all grid layout caches from SessionStorage
     * Called on logout to ensure fresh data on next login
     */
    clearAllCaches(): void {
        try {
            const keysToRemove: string[] = [];

            // Find all grid layout cache keys in SessionStorage
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.startsWith(this.CACHE_PREFIX) || key.startsWith(this.CACHE_VERSION_PREFIX))) {
                    keysToRemove.push(key);
                }
            }

            // Remove all found keys
            keysToRemove.forEach(key => {
                sessionStorage.removeItem(key);
            });

            if (keysToRemove.length > 0) {
                console.log(`Cleared ${keysToRemove.length} grid layout cache entries on logout`);
            }
        } catch (error) {
            console.error('Error clearing all grid layout caches:', error);
        }
    }
}
